import React from 'react';
import { Button, Card, Typography, Space, Tag } from 'antd';
import { AudioOutlined, StopOutlined, CheckCircleOutlined, SoundOutlined, RobotOutlined, UserOutlined, FileTextOutlined, ClockCircleOutlined } from '@ant-design/icons';

const { Title, Paragraph } = Typography;

interface IntroductionRecordingProps {
    isRecording: boolean;
    isLoading: boolean;
    isSubmitting: boolean;
    recordingTime: number;
    introductionBlob: Blob | null;
    transcription?: string;
    isTranscribing?: boolean;
    isSpeaking?: boolean;
    introductionVerificationFailed?: boolean;
    videoRef: React.RefObject<HTMLVideoElement>;
    streamRef: React.RefObject<MediaStream | null>;
    onStartRecording: () => void;
    onStopRecording: () => void;
    onStopAndSubmit?: () => void;
    onSaveRecording: (blob: Blob, transcript?: string) => void;
    onReRecord: () => void;
}

const IntroductionRecording: React.FC<IntroductionRecordingProps> = ({
    isRecording,
    isLoading,
    isSubmitting,
    recordingTime,
    introductionBlob,
    transcription = '',
    isTranscribing = false,
    isSpeaking = false,
    introductionVerificationFailed = false,
    videoRef,
    streamRef,
    onStartRecording,
    onStopRecording,
    onStopAndSubmit,
    onSaveRecording,
    onReRecord
}) => {
    return (
        <div className="bg-[#F3F7F7] flex h-screen flex-col overflow-hidden p-4">
            {/* Header */}
            <div className="flex justify-between items-center border-b border-gray-200 p-4 bg-white rounded-t-lg">
                <img src={require('./../../../assets/logo/logo.png')} alt="logo" className="w-40" />
                <div className="w-40"></div>
            </div>

            <div className="flex-1 flex gap-4 p-4">
                {/* First Column - AI Avatar */}
                <div className="w-1/3 flex flex-col gap-4">
                    {/* AI Avatar Area - Sufficient height */}
                    <div className="relative bg-black rounded-lg overflow-hidden flex items-center justify-center" style={{ minHeight: '350px', height: 'fit-content', padding: '40px' }}>
                        <div className="flex flex-col items-center justify-center">
                            <div className="w-32 h-32 rounded-full bg-gray-800 flex items-center justify-center mb-4">
                                <RobotOutlined style={{ fontSize: '64px', color: '#fff' }} />
                            </div>
                            <div className="text-white text-center">
                                {isRecording ? (
                                    <div className="flex flex-col items-center gap-2">
                                        <span className="text-lg font-semibold">Listening...</span>
                                        <div className="flex items-center gap-1">
                                            {[...Array(5)].map((_, i) => (
                                                <div
                                                    key={i}
                                                    className="w-1 bg-green-500 rounded-full"
                                                    style={{
                                                        animation: `listen 1s ease-in-out infinite`,
                                                        animationDelay: `${i * 0.1}s`,
                                                        height: '20px'
                                                    }}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                ) : (
                                    <span className="text-lg font-semibold">AI Interviewer</span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Second Column - Introduction Content */}
                <div className="w-2/3 flex flex-col min-w-0 overflow-hidden">
                    <Card
                        className="flex-1 flex flex-col shadow-lg overflow-auto"
                        style={{ borderRadius: '12px' }}
                        bodyStyle={{ display: 'flex', flexDirection: 'column', height: '100%', padding: '24px', overflow: 'auto' }}
                    >
                        <div className="flex-1 flex flex-col justify-center min-h-0">
                            {/* Header Section - Enhanced */}
                            <div className="mb-6 flex-shrink-0">
                                <div className="flex items-start gap-2 mb-2">
                                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center shadow-lg flex-shrink-0">
                                        <UserOutlined style={{ fontSize: '18px', color: '#fff' }} />
                                    </div>
                                    <div className="flex-1">
                                        <Title
                                            level={2}
                                            className="mb-0"
                                            style={{
                                                fontSize: '32px',
                                                fontWeight: 700,
                                                background: 'linear-gradient(135deg, #10b981 0%, #059669 50%, #047857 100%)',
                                                WebkitBackgroundClip: 'text',
                                                WebkitTextFillColor: 'transparent',
                                                backgroundClip: 'text',
                                                letterSpacing: '-0.5px',
                                                lineHeight: '1'
                                            }}
                                        >
                                            Introduction Recording
                                        </Title>
                                        <Paragraph
                                            className="text-sm text-gray-600 mb-0"
                                            style={{
                                                fontSize: '14px',
                                                lineHeight: '1.2',
                                                color: '#4b5563',
                                                marginTop: '0'
                                            }}
                                        >
                                            Please introduce yourself. Share your name, background, experience, and why you're interested in this role.
                                        </Paragraph>
                                    </div>
                                </div>
                                <div className="w-20 h-0.5 bg-gradient-to-r from-green-400 via-emerald-400 to-green-400 rounded-full"></div>
                            </div>

                            {/* Verification Failed Message */}
                            {introductionVerificationFailed && !isRecording && (
                                <div className="w-full mb-6 mx-auto">
                                    <div className="bg-red-50 border-2 border-red-300 rounded-xl p-5 shadow-md">
                                        <div className="flex items-start gap-3">
                                            <div className="flex-shrink-0 mt-1">
                                                <div className="w-8 h-8 rounded-full bg-red-500 flex items-center justify-center">
                                                    <span className="text-white font-bold text-lg">!</span>
                                                </div>
                                            </div>
                                            <div className="flex-1">
                                                <h4 className="font-semibold text-red-800 mb-2" style={{ fontSize: '16px' }}>
                                                    Introduction Requirements Not Met
                                                </h4>
                                                <p className="text-red-700 mb-0" style={{ fontSize: '14px', lineHeight: '1.6' }}>
                                                    Your introduction does not meet the requirements. Please ensure you record for at least 10 seconds and provide at least 50 words. Click "Re-record Introduction" to try again.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Instructions Box */}
                            <div className="bg-gradient-to-br from-green-50 via-emerald-50 to-green-50 border-2 border-green-200 rounded-xl p-6 mb-8 shadow-md hover:shadow-lg transition-shadow">
                                <div className="flex items-start gap-4">
                                    <div className="flex-shrink-0 mt-1">
                                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center shadow-md">
                                            <UserOutlined style={{ fontSize: '18px', color: '#fff' }} />
                                        </div>
                                    </div>
                                    <div className="flex-1">
                                        <Paragraph className="text-lg font-semibold text-gray-800 mb-3" style={{ lineHeight: '1.4' }}>
                                            What to include in your introduction:
                                        </Paragraph>
                                        <ul className="text-base text-gray-700 space-y-2 list-none" style={{ lineHeight: '1.6' }}>
                                            <li className="flex items-start gap-2">
                                                <span className="text-green-500 font-bold mt-1">•</span>
                                                <span>Your full name</span>
                                            </li>
                                            <li className="flex items-start gap-2">
                                                <span className="text-green-500 font-bold mt-1">•</span>
                                                <span>Your professional background and experience</span>
                                            </li>
                                            <li className="flex items-start gap-2">
                                                <span className="text-green-500 font-bold mt-1">•</span>
                                                <span>Your key skills and expertise</span>
                                            </li>
                                            <li className="flex items-start gap-2">
                                                <span className="text-green-500 font-bold mt-1">•</span>
                                                <span>Why you're interested in this role</span>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>

                            {/* Transcription Display */}
                            {(isRecording || transcription) && (
                                <div className="w-full  mb-6 mx-auto relative">
                                    <div
                                        className={`bg-white rounded-xl border-2 transition-all ${isRecording
                                            ? 'border-red-300 bg-red-50'
                                            : transcription
                                                ? 'border-green-300 bg-green-50'
                                                : 'border-gray-200 bg-gray-50'
                                            }`}
                                        style={{
                                            boxShadow: isRecording ? '0 0 20px rgba(239, 68, 68, 0.1)' : 'none'
                                        }}
                                    >
                                        {/* Header with Your Answer, Status Tags, and Timer */}
                                        <div className="flex items-center justify-between p-4 border-b border-gray-200 gap-3">
                                            {/* Left: Your Answer */}
                                            <div className="flex items-center gap-2">
                                                <UserOutlined style={{ fontSize: '18px', color: '#10b981' }} />
                                                <span className="font-semibold text-gray-800" style={{ fontSize: '16px' }}>
                                                    Your Answer
                                                </span>
                                            </div>

                                            {/* Middle: Status Tags */}
                                            <div className="flex items-center gap-2 flex-1 justify-center">
                                                {isRecording && (
                                                    <Tag
                                                        color="red"
                                                        style={{
                                                            border: '1px solid #ef4444',
                                                            background: '#fee2e2',
                                                            color: '#dc2626',
                                                            fontSize: '13px',
                                                            padding: '4px 12px',
                                                            borderRadius: '4px'
                                                        }}
                                                        icon={<AudioOutlined />}
                                                    >
                                                        Recording...
                                                    </Tag>
                                                )}
                                                {isTranscribing && (
                                                    <Tag
                                                        color="orange"
                                                        style={{
                                                            border: '1px solid #f97316',
                                                            background: '#fff7ed',
                                                            color: '#ea580c',
                                                            fontSize: '13px',
                                                            padding: '4px 12px',
                                                            borderRadius: '4px'
                                                        }}
                                                        icon={<SoundOutlined />}
                                                    >
                                                        Transcribing...
                                                    </Tag>
                                                )}
                                            </div>

                                            {/* Right: Timer */}
                                            {isRecording && (
                                                <div className="flex items-center gap-2">
                                                    <ClockCircleOutlined style={{ fontSize: '16px', color: '#ef4444' }} />
                                                    <span
                                                        className="text-base font-semibold"
                                                        style={{
                                                            color: '#ef4444'
                                                        }}
                                                    >
                                                        {Math.floor(recordingTime / 60)}:{(recordingTime % 60).toString().padStart(2, '0')}
                                                    </span>
                                                </div>
                                            )}
                                        </div>

                                        {/* Transcript Content */}
                                        <div
                                            className="p-5"
                                            style={{
                                                fontSize: '16px',
                                                lineHeight: '1.8',
                                                color: transcription ? '#1f2937' : '#9ca3af',
                                                fontStyle: transcription ? 'normal' : 'italic',
                                                whiteSpace: 'pre-wrap',
                                                wordBreak: 'break-word',
                                                minHeight: '150px',
                                                maxHeight: '300px',
                                                overflowY: 'auto'
                                            }}
                                        >
                                            {transcription || (
                                                <div className="flex flex-col items-center justify-center h-full text-center" style={{ minHeight: '150px' }}>
                                                    <div className="mb-3">
                                                        <AudioOutlined style={{ fontSize: '48px', color: '#ef4444' }} />
                                                    </div>
                                                    <p className="text-red-600 font-medium" style={{ fontSize: '16px' }}>Listening... Speak now</p>
                                                    <p className="text-sm text-gray-500 mt-1">Your speech will appear here</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Recording Controls */}
                            <div className="flex flex-col items-center gap-6">
                                {introductionBlob && !introductionVerificationFailed ? (
                                    // Recording successful - show nothing (will auto-advance)
                                    <></>
                                ) : (
                                    <>
                                        {!isRecording && (
                                            <Button
                                                type="primary"
                                                size="large"
                                                icon={<AudioOutlined />}
                                                onClick={onStartRecording}
                                                loading={isLoading}
                                                disabled={isSpeaking}
                                                style={{
                                                    height: '56px',
                                                    fontSize: '18px',
                                                    paddingLeft: '32px',
                                                    paddingRight: '32px',
                                                    borderRadius: '10px',
                                                    background: introductionVerificationFailed
                                                        ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                                                        : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                                    border: 'none',
                                                    boxShadow: introductionVerificationFailed
                                                        ? '0 4px 12px rgba(245, 158, 11, 0.3)'
                                                        : '0 4px 12px rgba(16, 185, 129, 0.3)',
                                                    opacity: isSpeaking ? 0.5 : 1,
                                                    cursor: isSpeaking ? 'not-allowed' : 'pointer'
                                                }}
                                            >
                                                {isSpeaking
                                                    ? 'Please wait...'
                                                    : introductionVerificationFailed
                                                        ? 'Re-record Introduction'
                                                        : 'Start Recording'}
                                            </Button>
                                        )}
                                        {isRecording && (
                                            <Button
                                                type="primary"
                                                size="large"
                                                icon={<StopOutlined />}
                                                onClick={onStopAndSubmit || onStopRecording}
                                                loading={isSubmitting}
                                                disabled={recordingTime < 10}
                                                style={{
                                                    height: '56px',
                                                    fontSize: '18px',
                                                    paddingLeft: '32px',
                                                    paddingRight: '32px',
                                                    borderRadius: '10px',
                                                    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                                                    border: 'none',
                                                    boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)',
                                                    opacity: recordingTime < 10 ? 0.5 : 1,
                                                    cursor: recordingTime < 10 ? 'not-allowed' : 'pointer'
                                                }}
                                            >
                                                {isSubmitting
                                                    ? 'Submitting...'
                                                    : recordingTime < 10
                                                        ? `Record at least 10s (${recordingTime}s)`
                                                        : 'Stop & Submit'}
                                            </Button>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default IntroductionRecording;
